import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CTABlock } from "@/components/CTABlock";
import { Calendar, Clock, ArrowLeft, Phone } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";
import { seoHead } from "@/lib/seo";
import { getPublicBlogPost } from "@/lib/cms.functions";
import { BlogAuthorBlock } from "@/components/BlogAuthorBlock";
import { WOODSEER_AUTHOR } from "@/lib/blog-author";

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  tags: string[];
  published_at: string | null;
  reading_minutes: number | null;
  meta_title: string | null;
  meta_description: string | null;
};

const SITE = "https://woodseerautos.co.uk";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const r = await getPublicBlogPost({ data: { slug: params.slug } });
    if (!r.post) throw notFound();
    return { post: r.post as Post };
  },
  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    if (!post) return {};
    const title = post.meta_title || post.title;
    const description = post.meta_description || post.excerpt || "";
    const pageUrl = `${SITE}/blog/${params.slug}`;
    const wordCount = post.content ? post.content.trim().split(/\s+/).length : undefined;
    const dateModified = post.published_at;
    const image = post.cover_image_url ? [post.cover_image_url] : undefined;
    return seoHead({
      path: `/blog/${params.slug}`,
      title,
      description,
      ogType: "article",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Blog", path: "/blog" },
        { name: post.title, path: `/blog/${params.slug}` },
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description,
          datePublished: post.published_at,
          ...(dateModified ? { dateModified } : {}),
          ...(image ? { image } : {}),
          ...(post.tags?.[0] ? { articleSection: post.tags[0] } : {}),
          ...(post.tags?.length ? { keywords: post.tags.join(", ") } : {}),
          ...(wordCount ? { wordCount } : {}),
          inLanguage: "en-GB",
          isAccessibleForFree: true,
          mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
          url: pageUrl,
          author: {
            "@type": "Person",
            name: WOODSEER_AUTHOR.name,
            jobTitle: WOODSEER_AUTHOR.role,
            description: WOODSEER_AUTHOR.bio,
            url: WOODSEER_AUTHOR.url,
            sameAs: [...WOODSEER_AUTHOR.sameAs],
            worksFor: {
              "@type": "AutoRepair",
              name: "Woodseer Autos",
              url: SITE,
              "@id": `${SITE}/#business`,
            },
          },
          publisher: {
            "@type": "Organization",
            name: "Woodseer Autos",
            url: SITE,
            "@id": `${SITE}/#business`,
            logo: {
              "@type": "ImageObject",
              url: `${SITE}/icon-512.png`,
            },
            sameAs: [
              "https://www.instagram.com/woodseerautos/",
              "https://linktr.ee/woodseerautos",
            ],
          },
        },
      ],
    });
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <div className="flex min-h-[50vh] items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground">Article Not Found</h1>
        <p className="mt-3 text-muted-foreground">That article doesn't exist. Browse our other posts instead.</p>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Blog
        </Link>
      </div>
    </div>
  ),
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  const biz = getBusinessConfig();
  const category = post.tags?.[0] ?? "Article";

  // Simple markdown rendering: ## headings, **bold**, [link](url), - lists, paragraphs
  const renderContent = (raw: string) => {
    const lines = raw.split("\n");
    const elements: React.ReactNode[] = [];
    let key = 0;

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      if (trimmed.startsWith("## ")) {
        elements.push(<h2 key={key++} className="text-xl font-bold text-foreground mt-8 mb-3">{trimmed.slice(3)}</h2>);
      } else if (trimmed.startsWith("- ")) {
        elements.push(<li key={key++} className="text-muted-foreground leading-relaxed ml-4 list-disc">{renderInline(trimmed.slice(2))}</li>);
      } else {
        elements.push(<p key={key++} className="text-muted-foreground leading-relaxed mb-4">{renderInline(trimmed)}</p>);
      }
    }
    return elements;
  };

  const renderInline = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const [, label, url] = linkMatch;
        if (url.startsWith("/")) {
          return <Link key={i} to={url} className="text-primary font-semibold underline-offset-4 hover:underline">{label}</Link>;
        }
        return <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold underline-offset-4 hover:underline">{label}</a>;
      }
      return part;
    });
  };

  return (
    <>
      <article className="py-12 md:py-20">
        <div className="mx-auto max-w-3xl px-4">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
            <ArrowLeft className="h-3.5 w-3.5" /> All articles
          </Link>

          <span className="inline-flex items-center text-xs font-medium text-primary bg-primary/5 px-2.5 py-1 rounded">{category}</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-foreground tracking-tight leading-tight">{post.title}</h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
            {post.published_at && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            )}
            {post.reading_minutes && (
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {post.reading_minutes} min read
              </span>
            )}
          </div>

          <div className="mt-10">{renderContent(post.content)}</div>

          <BlogAuthorBlock />

          <div className="mt-12 p-6 rounded-xl border border-primary/20 bg-primary/5">
            <p className="text-lg font-bold text-foreground">Need help with this?</p>
            <p className="mt-1 text-sm text-muted-foreground">Call us on {biz.phone} or request a booking online.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <a
                href={biz.phoneHref}
                onClick={() => trackEvent("call_click", { location: "blog_post" })}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Phone className="h-4 w-4" />
                Call {biz.phone}
              </a>
              <Link
                to="/book"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-secondary transition-colors"
              >
                Request a Booking
              </Link>
            </div>
          </div>
        </div>
      </article>

      <CTABlock headline="Questions About Your Vehicle?" subtext="We're always happy to help — no pressure, just honest advice." />
    </>
  );
}
